import React, { useState, useEffect } from 'react';
import {
  ChevronRightIcon,
  ChevronDownIcon,
} from './editorIcons';
import { Image as ImageIcon, X, RotateCw, RotateCcw, FlipHorizontal, FlipVertical, RefreshCw } from 'lucide-react';
import { WRAP_OPTIONS } from './ImageWrapDropdown';

// Recolor presets definitions using CSS filters
export const RECOLOR_PRESETS = [
  { id: 'none', label: 'No recolour', filter: 'none' },
  { id: 'grayscale', label: 'Grayscale', filter: 'grayscale(100%)' },
  { id: 'sepia', label: 'Sepia', filter: 'sepia(100%)' },
  { id: 'invert', label: 'Negative / Invert', filter: 'invert(100%)' },
  { id: 'warm', label: 'Warm / Amber', filter: 'sepia(50%) contrast(110%) brightness(95%)' },
  { id: 'cool', label: 'Cool / Cyan', filter: 'hue-rotate(180deg) saturate(130%)' },
  { id: 'vintage', label: 'Vintage', filter: 'sepia(30%) contrast(90%) brightness(105%)' },
  { id: 'high-contrast', label: 'High contrast', filter: 'contrast(160%)' },
  { id: 'bw', label: 'Black & white', filter: 'grayscale(100%) contrast(250%)' },
  { id: 'soft-fade', label: 'Soft fade', filter: 'opacity(0.85) contrast(90%) brightness(105%)' },
];

/**
 * ImageOptionsSidebar
 * Matches Image 5:
 * Accordion sections:
 * > Size and rotation
 * > Text wrapping
 * > Position
 * > Re-colour
 * > Adjustments
 * > Alt text
 */
const ImageOptionsSidebar = ({
  isOpen,
  onClose,
  selectedImg,
  currentWrapMode,
  onSelectWrapMode,
  onChange,
}) => {
  // Accordion open/close state (matching Image 5)
  const [openSections, setOpenSections] = useState({
    size: true, // Default open for quick access
    wrap: false,
    position: false,
    recolor: false,
    adjustments: false,
    altText: false,
  });

  // Size state
  const [width, setWidth] = useState(300);
  const [height, setHeight] = useState(200);
  const [lockAspect, setLockAspect] = useState(true);
  const [scaleW, setScaleW] = useState(100);
  const [scaleH, setScaleH] = useState(100);

  // Rotation & Flip state
  const [rotateAngle, setRotateAngle] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);

  // Text Wrapping Margin state
  const [margin, setMargin] = useState('16px');

  // Position state
  const [positionMode, setPositionMode] = useState('move'); // 'move' or 'fixed'
  const [align, setAlign] = useState('left'); // 'left', 'center', 'right'

  // Recolor state
  const [recolorId, setRecolorId] = useState('none');

  // Adjustments state
  const [opacity, setOpacity] = useState(100);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [blur, setBlur] = useState(0);

  // Alt Text state
  const [altText, setAltText] = useState('');
  const [titleText, setTitleText] = useState('');

  // Read current image attributes and styles when selectedImg changes
  useEffect(() => {
    if (!selectedImg) return;

    // Dimensions
    const naturalW = selectedImg.naturalWidth || 400;
    const naturalH = selectedImg.naturalHeight || 300;
    const currentW = selectedImg.offsetWidth || parseInt(selectedImg.style.width) || naturalW;
    const currentH = selectedImg.offsetHeight || parseInt(selectedImg.style.height) || naturalH;

    setWidth(Math.round(currentW));
    setHeight(Math.round(currentH));
    setScaleW(Math.round((currentW / (naturalW || 1)) * 100));
    setScaleH(Math.round((currentH / (naturalH || 1)) * 100));

    // Rotation & Flip from data attributes or transform
    const savedRotate = parseInt(selectedImg.getAttribute('data-rotate') || '0', 10);
    setRotateAngle(isNaN(savedRotate) ? 0 : savedRotate);
    setFlipH(selectedImg.getAttribute('data-flip-h') === 'true');
    setFlipV(selectedImg.getAttribute('data-flip-v') === 'true');

    // Margin
    const savedMargin = selectedImg.getAttribute('data-margin') || selectedImg.style.margin || '16px';
    const numMargin = parseInt(savedMargin, 10);
    setMargin(!isNaN(numMargin) ? `${numMargin}px` : '16px');

    // Alignment
    if (selectedImg.style.float === 'right') setAlign('right');
    else if (selectedImg.style.float === 'left') setAlign('left');
    else if (selectedImg.style.margin === '16px auto' || selectedImg.style.marginLeft === 'auto') setAlign('center');
    else setAlign('left');

    // Recolor
    const savedRecolor = selectedImg.getAttribute('data-recolor') || 'none';
    setRecolorId(savedRecolor);

    // Adjustments
    const savedOpacity = parseInt(selectedImg.getAttribute('data-opacity') || '100', 10);
    const savedBrightness = parseInt(selectedImg.getAttribute('data-brightness') || '100', 10);
    const savedContrast = parseInt(selectedImg.getAttribute('data-contrast') || '100', 10);
    const savedBlur = parseInt(selectedImg.getAttribute('data-blur') || '0', 10);

    setOpacity(savedOpacity);
    setBrightness(savedBrightness);
    setContrast(savedContrast);
    setBlur(savedBlur);

    // Alt text & title
    setAltText(selectedImg.getAttribute('alt') || '');
    setTitleText(selectedImg.getAttribute('title') || '');
  }, [selectedImg]);

  const toggleSection = (key) => {
    setOpenSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Helper to compile CSS transform from rotation and flips
  const applyTransform = (angle, fH, fV) => {
    if (!selectedImg) return;
    const parts = [];
    if (angle !== 0) parts.push(`rotate(${angle}deg)`);
    if (fH && fV) parts.push('scale(-1, -1)');
    else if (fH) parts.push('scaleX(-1)');
    else if (fV) parts.push('scaleY(-1)');

    selectedImg.style.transform = parts.length > 0 ? parts.join(' ') : 'none';
    selectedImg.setAttribute('data-rotate', angle.toString());
    selectedImg.setAttribute('data-flip-h', fH ? 'true' : 'false');
    selectedImg.setAttribute('data-flip-v', fV ? 'true' : 'false');
    if (onChange) onChange();
  };

  // Helper to compile CSS filter from adjustments and recolor
  const applyFilter = (rId, op, br, co, bl) => {
    if (!selectedImg) return;
    const preset = RECOLOR_PRESETS.find((p) => p.id === rId);
    const filters = [];

    if (preset && preset.filter !== 'none') {
      filters.push(preset.filter);
    }
    if (op !== 100) filters.push(`opacity(${op / 100})`);
    if (br !== 100) filters.push(`brightness(${br / 100})`);
    if (co !== 100) filters.push(`contrast(${co / 100})`);
    if (bl > 0) filters.push(`blur(${bl}px)`);

    selectedImg.style.filter = filters.length > 0 ? filters.join(' ') : 'none';
    selectedImg.setAttribute('data-recolor', rId);
    selectedImg.setAttribute('data-opacity', op.toString());
    selectedImg.setAttribute('data-brightness', br.toString());
    selectedImg.setAttribute('data-contrast', co.toString());
    selectedImg.setAttribute('data-blur', bl.toString());
    if (onChange) onChange();
  };

  // Size handlers
  const handleWidthChange = (val) => {
    const num = Math.max(30, Number(val) || 30);
    setWidth(num);
    if (!selectedImg) return;

    selectedImg.style.width = `${num}px`;
    selectedImg.style.maxWidth = '100%';

    if (lockAspect && height > 0 && width > 0) {
      const ratio = height / width;
      const newH = Math.round(num * ratio);
      setHeight(newH);
      selectedImg.style.height = `${newH}px`;
    }
    if (onChange) onChange();
  };

  const handleHeightChange = (val) => {
    const num = Math.max(20, Number(val) || 20);
    setHeight(num);
    if (!selectedImg) return;

    selectedImg.style.height = `${num}px`;

    if (lockAspect && width > 0 && height > 0) {
      const ratio = width / height;
      const newW = Math.round(num * ratio);
      setWidth(newW);
      selectedImg.style.width = `${newW}px`;
    }
    if (onChange) onChange();
  };

  const handlePresetSize = (presetWidth) => {
    if (!selectedImg) return;
    if (presetWidth === '100%') {
      selectedImg.style.width = '100%';
      selectedImg.style.height = 'auto';
      setWidth(Math.round(selectedImg.offsetWidth || 600));
      setHeight(Math.round(selectedImg.offsetHeight || 400));
    } else {
      const num = parseInt(presetWidth, 10);
      handleWidthChange(num);
    }
    if (onChange) onChange();
  };

  // Rotation handlers
  const rotateClockwise = () => {
    const newAngle = (rotateAngle + 90) % 360;
    setRotateAngle(newAngle);
    applyTransform(newAngle, flipH, flipV);
  };

  const rotateCounterClockwise = () => {
    const newAngle = (rotateAngle - 90 + 360) % 360;
    setRotateAngle(newAngle);
    applyTransform(newAngle, flipH, flipV);
  };

  const handleAngleSlider = (val) => {
    const angle = Number(val) || 0;
    setRotateAngle(angle);
    applyTransform(angle, flipH, flipV);
  };

  const toggleFlipH = () => {
    const newF = !flipH;
    setFlipH(newF);
    applyTransform(rotateAngle, newF, flipV);
  };

  const toggleFlipV = () => {
    const newF = !flipV;
    setFlipV(newF);
    applyTransform(rotateAngle, flipH, newF);
  };

  // Margin handler for text wrapping
  const handleMarginChange = (val) => {
    setMargin(val);
    if (!selectedImg) return;
    selectedImg.setAttribute('data-margin', val);
    if (currentWrapMode === 'wrap-text') {
      selectedImg.style.margin = `8px ${val} 8px 0`;
    } else if (currentWrapMode === 'break-text') {
      selectedImg.style.margin = `${val} auto`;
    } else {
      selectedImg.style.margin = val;
    }
    if (onChange) onChange();
  };

  // Position alignment handler
  const handleAlign = (newAlign) => {
    setAlign(newAlign);
    if (!selectedImg) return;
    if (newAlign === 'left') {
      selectedImg.style.float = 'left';
      selectedImg.style.margin = `8px ${margin} 8px 0`;
      selectedImg.style.display = 'block';
    } else if (newAlign === 'center') {
      selectedImg.style.float = 'none';
      selectedImg.style.margin = `${margin} auto`;
      selectedImg.style.display = 'block';
    } else if (newAlign === 'right') {
      selectedImg.style.float = 'right';
      selectedImg.style.margin = `8px 0 8px ${margin}`;
      selectedImg.style.display = 'block';
    }
    if (onChange) onChange();
  };

  // Recolor handler
  const handleRecolorSelect = (rId) => {
    setRecolorId(rId);
    applyFilter(rId, opacity, brightness, contrast, blur);
  };

  // Adjustments handlers
  const handleOpacityChange = (val) => {
    const v = Number(val);
    setOpacity(v);
    applyFilter(recolorId, v, brightness, contrast, blur);
  };

  const handleBrightnessChange = (val) => {
    const v = Number(val);
    setBrightness(v);
    applyFilter(recolorId, opacity, v, contrast, blur);
  };

  const handleContrastChange = (val) => {
    const v = Number(val);
    setContrast(v);
    applyFilter(recolorId, opacity, brightness, v, blur);
  };

  const handleBlurChange = (val) => {
    const v = Number(val);
    setBlur(v);
    applyFilter(recolorId, opacity, brightness, contrast, v);
  };

  const handleResetAdjustments = () => {
    setOpacity(100);
    setBrightness(100);
    setContrast(100);
    setBlur(0);
    setRecolorId('none');
    applyFilter('none', 100, 100, 100, 0);
  };

  // Alt Text & Title handler
  const handleApplyAltText = () => {
    if (!selectedImg) return;
    selectedImg.setAttribute('alt', altText);
    selectedImg.setAttribute('title', titleText);
    if (onChange) onChange();
  };

  // Close on Escape key press
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="rte-gdocs-sidebar" onClick={(e) => e.stopPropagation()}>
      {/* Sidebar Header (Image 5) */}
      <div className="rte-gdocs-sidebar-header">
        <div className="rte-gdocs-sidebar-title">
          <ImageIcon className="w-5 h-5 text-gray-700 mr-2" />
          <span>Image options</span>
        </div>
        <button
          type="button"
          className="rte-gdocs-sidebar-close"
          onClick={onClose}
          title="Close Image options (Esc)"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="rte-gdocs-sidebar-content">
        {/* Section 1: Size and rotation */}
        <div className="rte-gdocs-accordion">
          <button
            type="button"
            className="rte-gdocs-accordion-header"
            onClick={() => toggleSection('size')}
          >
            <span className="rte-gdocs-accordion-icon">
              {openSections.size ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
            </span>
            <span className="rte-gdocs-accordion-label">Size and rotation</span>
          </button>

          {openSections.size && (
            <div className="rte-gdocs-accordion-body">
              {/* Size preset chips */}
              <div className="rte-gdocs-field-group">
                <label className="rte-gdocs-label">Quick size presets</label>
                <div className="rte-gdocs-chip-group">
                  <button
                    type="button"
                    className="rte-gdocs-chip"
                    onClick={() => handlePresetSize('150px')}
                  >
                    Small (150px)
                  </button>
                  <button
                    type="button"
                    className="rte-gdocs-chip"
                    onClick={() => handlePresetSize('320px')}
                  >
                    Medium (320px)
                  </button>
                  <button
                    type="button"
                    className="rte-gdocs-chip"
                    onClick={() => handlePresetSize('520px')}
                  >
                    Large (520px)
                  </button>
                  <button
                    type="button"
                    className="rte-gdocs-chip"
                    onClick={() => handlePresetSize('100%')}
                  >
                    Full width
                  </button>
                </div>
              </div>

              {/* Exact Width & Height inputs */}
              <div className="rte-gdocs-row">
                <div className="rte-gdocs-input-col">
                  <label className="rte-gdocs-sublabel">Width (px)</label>
                  <input
                    type="number"
                    min="30"
                    max="1600"
                    value={width}
                    onChange={(e) => handleWidthChange(e.target.value)}
                    className="rte-gdocs-input"
                  />
                </div>
                <div className="rte-gdocs-input-col">
                  <label className="rte-gdocs-sublabel">Height (px)</label>
                  <input
                    type="number"
                    min="20"
                    max="1600"
                    value={height}
                    onChange={(e) => handleHeightChange(e.target.value)}
                    className="rte-gdocs-input"
                  />
                </div>
              </div>

              {/* Lock aspect ratio checkbox */}
              <label className="rte-gdocs-checkbox-label">
                <input
                  type="checkbox"
                  checked={lockAspect}
                  onChange={(e) => setLockAspect(e.target.checked)}
                />
                <span>Lock aspect ratio</span>
              </label>

              {/* Scale % inputs matching Google Docs */}
              <div className="rte-gdocs-row mt-1">
                <div className="rte-gdocs-input-col">
                  <label className="rte-gdocs-sublabel">Width scale (%)</label>
                  <input
                    type="number"
                    min="10"
                    max="500"
                    value={scaleW}
                    onChange={(e) => {
                      const newScale = Number(e.target.value) || 100;
                      setScaleW(newScale);
                      const natW = selectedImg?.naturalWidth || 400;
                      handleWidthChange(Math.round((natW * newScale) / 100));
                    }}
                    className="rte-gdocs-input"
                  />
                </div>
                <div className="rte-gdocs-input-col">
                  <label className="rte-gdocs-sublabel">Height scale (%)</label>
                  <input
                    type="number"
                    min="10"
                    max="500"
                    value={scaleH}
                    onChange={(e) => {
                      const newScale = Number(e.target.value) || 100;
                      setScaleH(newScale);
                      const natH = selectedImg?.naturalHeight || 300;
                      handleHeightChange(Math.round((natH * newScale) / 100));
                    }}
                    className="rte-gdocs-input"
                  />
                </div>
              </div>

              <div className="rte-gdocs-divider" />

              {/* Rotate section */}
              <div className="rte-gdocs-field-group">
                <label className="rte-gdocs-label">Rotate & Flip</label>
                <div className="rte-gdocs-rotate-controls">
                  <button
                    type="button"
                    className="rte-gdocs-action-btn"
                    onClick={rotateClockwise}
                    title="Rotate 90° clockwise"
                  >
                    <RotateCw className="w-3.5 h-3.5 mr-1" />
                    <span>90°</span>
                  </button>
                  <button
                    type="button"
                    className="rte-gdocs-action-btn"
                    onClick={rotateCounterClockwise}
                    title="Rotate 90° counter-clockwise"
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1" />
                    <span>-90°</span>
                  </button>
                  <button
                    type="button"
                    className={`rte-gdocs-action-btn ${flipH ? 'active' : ''}`}
                    onClick={toggleFlipH}
                    title="Flip horizontally"
                  >
                    <FlipHorizontal className="w-3.5 h-3.5 mr-1" />
                    <span>Flip H</span>
                  </button>
                  <button
                    type="button"
                    className={`rte-gdocs-action-btn ${flipV ? 'active' : ''}`}
                    onClick={toggleFlipV}
                    title="Flip vertically"
                  >
                    <FlipVertical className="w-3.5 h-3.5 mr-1" />
                    <span>Flip V</span>
                  </button>
                </div>

                {/* Angle slider */}
                <div className="rte-gdocs-slider-row mt-2">
                  <span className="rte-gdocs-sublabel">Angle:</span>
                  <input
                    type="range"
                    min="0"
                    max="360"
                    value={rotateAngle}
                    onChange={(e) => handleAngleSlider(e.target.value)}
                    className="rte-gdocs-slider"
                  />
                  <span className="rte-gdocs-slider-num">{rotateAngle}°</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Section 2: Text wrapping */}
        <div className="rte-gdocs-accordion">
          <button
            type="button"
            className="rte-gdocs-accordion-header"
            onClick={() => toggleSection('wrap')}
          >
            <span className="rte-gdocs-accordion-icon">
              {openSections.wrap ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
            </span>
            <span className="rte-gdocs-accordion-label">Text wrapping</span>
          </button>

          {openSections.wrap && (
            <div className="rte-gdocs-accordion-body">
              <label className="rte-gdocs-label">Wrapping style</label>
              <div className="rte-gdocs-wrap-cards-grid">
                {WRAP_OPTIONS.map((opt) => {
                  const OptIcon = opt.icon;
                  const isSelected = currentWrapMode === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      className={`rte-gdocs-wrap-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => onSelectWrapMode(opt.id)}
                      title={opt.description}
                    >
                      <OptIcon className="rte-gdocs-wrap-card-icon" />
                      <span className="rte-gdocs-wrap-card-label">{opt.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Margin from text */}
              <div className="rte-gdocs-field-group mt-3">
                <label className="rte-gdocs-label">Margin from text</label>
                <div className="rte-gdocs-chip-group">
                  {['0px', '4px', '8px', '16px', '24px', '32px'].map((m) => (
                    <button
                      key={m}
                      type="button"
                      className={`rte-gdocs-chip ${margin === m ? 'active' : ''}`}
                      onClick={() => handleMarginChange(m)}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Section 3: Position */}
        <div className="rte-gdocs-accordion">
          <button
            type="button"
            className="rte-gdocs-accordion-header"
            onClick={() => toggleSection('position')}
          >
            <span className="rte-gdocs-accordion-icon">
              {openSections.position ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
            </span>
            <span className="rte-gdocs-accordion-label">Position</span>
          </button>

          {openSections.position && (
            <div className="rte-gdocs-accordion-body">
              <div className="rte-gdocs-radio-group">
                <label className="rte-gdocs-radio-label">
                  <input
                    type="radio"
                    name="rte-position-mode"
                    value="move"
                    checked={positionMode === 'move'}
                    onChange={() => setPositionMode('move')}
                  />
                  <span>Move with text</span>
                </label>
                <label className="rte-gdocs-radio-label">
                  <input
                    type="radio"
                    name="rte-position-mode"
                    value="fixed"
                    checked={positionMode === 'fixed'}
                    onChange={() => setPositionMode('fixed')}
                  />
                  <span>Fix position on page</span>
                </label>
              </div>

              <div className="rte-gdocs-divider" />

              <label className="rte-gdocs-label">Alignment</label>
              <div className="rte-gdocs-chip-group">
                <button
                  type="button"
                  className={`rte-gdocs-chip ${align === 'left' ? 'active' : ''}`}
                  onClick={() => handleAlign('left')}
                >
                  ⬅ Float Left
                </button>
                <button
                  type="button"
                  className={`rte-gdocs-chip ${align === 'center' ? 'active' : ''}`}
                  onClick={() => handleAlign('center')}
                >
                  ⏹ Center Block
                </button>
                <button
                  type="button"
                  className={`rte-gdocs-chip ${align === 'right' ? 'active' : ''}`}
                  onClick={() => handleAlign('right')}
                >
                  Float Right ➡
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Section 4: Re-colour */}
        <div className="rte-gdocs-accordion">
          <button
            type="button"
            className="rte-gdocs-accordion-header"
            onClick={() => toggleSection('recolor')}
          >
            <span className="rte-gdocs-accordion-icon">
              {openSections.recolor ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
            </span>
            <span className="rte-gdocs-accordion-label">Re-colour</span>
          </button>

          {openSections.recolor && (
            <div className="rte-gdocs-accordion-body">
              <label className="rte-gdocs-label">Recolour filter</label>
              <div className="rte-gdocs-recolor-grid">
                {RECOLOR_PRESETS.map((preset) => {
                  const isSelected = recolorId === preset.id;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      className={`rte-gdocs-recolor-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleRecolorSelect(preset.id)}
                      title={preset.label}
                    >
                      <div
                        className="rte-gdocs-recolor-preview"
                        style={{
                          filter: preset.filter,
                          backgroundImage: selectedImg?.src ? `url(${selectedImg.src})` : 'none',
                        }}
                      />
                      <span className="rte-gdocs-recolor-name">{preset.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Section 5: Adjustments */}
        <div className="rte-gdocs-accordion">
          <button
            type="button"
            className="rte-gdocs-accordion-header"
            onClick={() => toggleSection('adjustments')}
          >
            <span className="rte-gdocs-accordion-icon">
              {openSections.adjustments ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
            </span>
            <span className="rte-gdocs-accordion-label">Adjustments</span>
          </button>

          {openSections.adjustments && (
            <div className="rte-gdocs-accordion-body">
              {/* Opacity Slider */}
              <div className="rte-gdocs-slider-item">
                <div className="rte-gdocs-slider-header">
                  <span>Transparency / Opacity</span>
                  <span className="rte-gdocs-slider-val">{opacity}%</span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="100"
                  value={opacity}
                  onChange={(e) => handleOpacityChange(e.target.value)}
                  className="rte-gdocs-slider"
                />
              </div>

              {/* Brightness Slider */}
              <div className="rte-gdocs-slider-item">
                <div className="rte-gdocs-slider-header">
                  <span>Brightness</span>
                  <span className="rte-gdocs-slider-val">{brightness}%</span>
                </div>
                <input
                  type="range"
                  min="20"
                  max="200"
                  value={brightness}
                  onChange={(e) => handleBrightnessChange(e.target.value)}
                  className="rte-gdocs-slider"
                />
              </div>

              {/* Contrast Slider */}
              <div className="rte-gdocs-slider-item">
                <div className="rte-gdocs-slider-header">
                  <span>Contrast</span>
                  <span className="rte-gdocs-slider-val">{contrast}%</span>
                </div>
                <input
                  type="range"
                  min="20"
                  max="200"
                  value={contrast}
                  onChange={(e) => handleContrastChange(e.target.value)}
                  className="rte-gdocs-slider"
                />
              </div>

              {/* Blur Slider */}
              <div className="rte-gdocs-slider-item">
                <div className="rte-gdocs-slider-header">
                  <span>Soft Blur</span>
                  <span className="rte-gdocs-slider-val">{blur}px</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={blur}
                  onChange={(e) => handleBlurChange(e.target.value)}
                  className="rte-gdocs-slider"
                />
              </div>

              {/* Reset button */}
              <button
                type="button"
                className="rte-gdocs-reset-btn"
                onClick={handleResetAdjustments}
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1" />
                <span>Reset adjustments</span>
              </button>
            </div>
          )}
        </div>

        {/* Section 6: Alt text */}
        <div className="rte-gdocs-accordion">
          <button
            type="button"
            className="rte-gdocs-accordion-header"
            onClick={() => toggleSection('altText')}
          >
            <span className="rte-gdocs-accordion-icon">
              {openSections.altText ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
            </span>
            <span className="rte-gdocs-accordion-label">Alt text</span>
          </button>

          {openSections.altText && (
            <div className="rte-gdocs-accordion-body">
              <div className="rte-gdocs-field-group">
                <label className="rte-gdocs-sublabel">Title</label>
                <input
                  type="text"
                  placeholder="Image title attribute"
                  value={titleText}
                  onChange={(e) => setTitleText(e.target.value)}
                  className="rte-gdocs-input"
                />
              </div>

              <div className="rte-gdocs-field-group">
                <label className="rte-gdocs-sublabel">Description (Alt text)</label>
                <textarea
                  rows="3"
                  placeholder="Alt text description for accessibility and SEO"
                  value={altText}
                  onChange={(e) => setAltText(e.target.value)}
                  className="rte-gdocs-textarea"
                />
              </div>

              <p className="rte-gdocs-hint">
                Alt text is accessed by screen readers for people who might have trouble seeing your content, and helps search engine ranking.
              </p>

              <button
                type="button"
                className="rte-gdocs-apply-btn"
                onClick={handleApplyAltText}
              >
                Apply alt text
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageOptionsSidebar;
